import {
  Injectable,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { AnimationService, styleVariables } from '@progleasing/grit-core';

import {
  PageTransitionOptions,
  PageTransitionTarget,
  RenderedViews,
} from '../types';

export abstract class PageTransitionStrategy {
  abstract goToPage(
    from: RenderedViews,
    to: PageTransitionTarget,
    opts: PageTransitionOptions
  ): Promise<RenderedViews>;
}

const speedMultiplier = 1;

@Injectable()
export class DefaultPageTransitionStrategy implements PageTransitionStrategy {
  get pageTransitionDuration() {
    return 150 / speedMultiplier;
  }

  get pageTransitionDelay() {
    return 150 / speedMultiplier;
  }

  get pageTransitionEasing() {
    return styleVariables.transitionCurveStandard;
  }
  constructor(
    public animation: AnimationService,
    private renderer: Renderer2
  ) {}

  goToPage(
    from: RenderedViews,
    to: PageTransitionTarget,
    opts: PageTransitionOptions
  ) {
    const {
      forward,
      pageBodyOutlet,
      pageTitleOutlet,
      sectionTitleOutlet,
      pageTitleParent,
      pageBodyParent,
    } = opts;

    let sectionPromise: any;
    let sectionTitleViewRef = from.sectionTitleViewRef;
    let sectionChanged = false;
    const fromSectionTitleElement = this._getAnimatableElementFromViewRef(
      from.sectionTitleViewRef
    );
    if (from.section !== to.section) {
      // Run the section animations
      sectionChanged = true;
      const sectionTitleView = this._createTransitionTarget(
        to.sectionTitleTemplate,
        opts.sectionTitleOutlet
      );

      sectionTitleViewRef = sectionTitleView.embeddedViewRef;
      sectionTitleView.element.style.opacity = 0;
      sectionPromise = this._animateSectionTitle(
        fromSectionTitleElement,
        sectionTitleView.element,
        opts
      );
    } else if (!opts.isLargeViewport) {
      setTimeout(() => {
        // TODO: this is an absolute garbage way to do all of this.
        // This need to be thought out more.
        window.scrollTo({
          top: fromSectionTitleElement.parentElement.parentElement.offsetHeight,
          behavior: 'smooth',
        });
      }, 0);
    }

    // set the parent elements to their static height.
    this._setStaticHeight(pageBodyParent.nativeElement);
    this._setStaticHeight(pageTitleParent.nativeElement);
    // Create the elements we are going to transition to
    // because they are only templates right now.]
    const fromPageBodyElement: HTMLElement = this._getAnimatableElementFromViewRef(
      from.pageBodyViewRef
    );
    const fromPageTitleElement: HTMLElement = this._getAnimatableElementFromViewRef(
      from.pageTitleViewRef
    );
    const toPageBodyView = this._createTransitionTarget(
      to.pageBodyTemplate,
      pageBodyOutlet
    );
    this.renderer.setStyle(toPageBodyView.element, 'visibility', 'hidden');

    const toPageTitleView: any =
      this._createTransitionTarget(to.pageTitleTemplate, pageTitleOutlet) || {};
    if (toPageTitleView.element) {
      this.renderer.setStyle(toPageTitleView.element, 'visibility', 'hidden');
    }

    return new Promise(res => {
      // We put this all in a timeout to let the
      // target page fully render before running the animation
      setTimeout(() => {
        const promises = [
          sectionPromise,
          this._animatePageTitle(
            fromPageTitleElement,
            toPageTitleView.element,
            pageTitleParent.nativeElement,
            forward
          ),
          this._animatePageBody(
            fromPageBodyElement,
            toPageBodyView.element,
            pageBodyParent.nativeElement,
            forward
          ),
        ];
        // At this point, the from object should alreaady have rendered components
        // So we are going to render the templates in the "to" object
        // That way we have a tangible element to apply animations to.

        Promise.all(promises)
          .then(() => {
            // remove the static heights
            this._removeStaticHeight(pageBodyParent.nativeElement);
            this._removeStaticHeight(pageTitleParent.nativeElement);
            from.pageBodyViewRef.destroy();
            if (from && from.pageTitleViewRef) {
              from.pageTitleViewRef.destroy();
            }
            if (sectionChanged) {
              from.sectionTitleViewRef.destroy();
            }

            return {
              section: to.section,
              page: to.page,
              sectionTitleViewRef: sectionTitleViewRef,
              pageBodyViewRef: toPageBodyView.embeddedViewRef,
              pageTitleViewRef: toPageTitleView.embeddedViewRef,
              sectionChanged,
            };
          })
          .then(data => res(data));
      }, 150);
    });
  }

  // Adds the target element to the
  private _addTemplateToOutlet(
    outlet: ViewContainerRef,
    template: TemplateRef<any>
  ) {}

  private _animateSectionTitle(
    from: HTMLElement,
    to: HTMLElement,
    opts: PageTransitionOptions
  ) {
    this._preAnimationStyles_From(from, opts.forward);
    this._preAnimationStylesSetion_To(to, opts.forward);

    const promises = [
      this.animation
        .animate({
          targets: from,
          transform: this._getPostAnimationTransform_From(opts.forward),
          opacity: 0,
          duration: this.pageTransitionDuration,
          easing: this.pageTransitionEasing,
          fillMode: 'forwards',
        })
        .then((e: any) => {
          if (e && e.length) {
            this._postAnimationStyles_From(e[0].element);
            e[0].animation.destroy();
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return opts.layoutStrategy.reconcilePageLayout();
        }),

      this.animation
        .animate({
          targets: to,
          transform: `translate(0) scale(1)`,
          opacity: 1,
          duration: this.pageTransitionDuration,
          delay: this.pageTransitionDelay,
          easing: this.pageTransitionEasing,
          fillMode: 'forwards',
        })
        .then((e: any) => {
          if (e && e.length) {
            this._postAnimationStyles_To(e[0].element);
            e[0].animation.destroy();
          }
        }),
    ];

    return Promise.all(promises);
  }

  private _animatePageTitle(
    from: HTMLElement,
    to: HTMLElement,
    parent: HTMLElement,
    forward: boolean
  ) {
    return this._pageAnimation(from, to, parent, forward);
  }

  private _animatePageBody(
    from: HTMLElement,
    to: HTMLElement,
    parent: HTMLElement,
    forward: boolean
  ) {
    return this._pageAnimation(from, to, parent, forward);
  }

  private _pageAnimation(
    from: HTMLElement,
    to: HTMLElement,
    parent: HTMLElement,
    forward: boolean
  ) {
    this._preAnimationStyles_To(to, forward);
    this._preAnimationStyles_From(from, forward);

    const targetElementHeight = to ? to.offsetHeight : 0;
    const fromAnimPromise = from
      ? this.animation
          .animate({
            targets: from,
            transform: this._getPostAnimationTransform_From(forward),
            opacity: 0,
            duration: this.pageTransitionDuration,
            easing: this.pageTransitionEasing,
            fillMode: 'forwards',
          })
          .then((e: any) => {
            if (e && e.length) {
              this._postAnimationStyles_From(e[0].element);
              e[0].animation.destroy();
            }
          })
      : Promise.resolve();

    const parentAnimPromise = this.animation
      .animate({
        targets: parent,
        height: targetElementHeight + 1,
        duration: this.pageTransitionDuration,
        delay: this.pageTransitionDelay / 2,
        easing: this.pageTransitionEasing,
        fillMode: 'forwards',
      })
      .then((e: any) => {
        if (e && e.length) {
          this.renderer.setStyle(
            e[0].element,
            'height',
            `${targetElementHeight + 1}px`
          );
          e[0].animation.destroy();
        }
      });

    const toAnimPromise = to
      ? this.animation
          .animate({
            targets: to || {},
            transform: `translate(0) scale(1)`,
            opacity: 1,
            duration: this.pageTransitionDuration,
            delay: this.pageTransitionDelay,
            easing: this.pageTransitionEasing,
            fillMode: 'forwards',
          })
          .then((e: any) => {
            if (e && e.length) {
              this._postAnimationStyles_To(e[0].element);
              e[0].animation.destroy();
            }
          })
      : Promise.resolve();
    const promises = [fromAnimPromise, parentAnimPromise, toAnimPromise];

    return Promise.all(promises);
  }

  private _getAnimatableElementFromViewRef(v: any) {
    if (!v) {
      return null;
    }
    return v.rootNodes.find(n => n.nodeName !== '#comment');
  }

  private _createTransitionTarget(
    template: TemplateRef<any>,
    outlet: ViewContainerRef
  ) {
    if (!template || !outlet) {
      return null;
    }
    const vr = outlet.createEmbeddedView(template);
    const node = this._getAnimatableElementFromViewRef(vr);
    node.style.opacity = 0;
    return {
      embeddedViewRef: vr,
      element: node,
    };
  }

  private _preAnimationStyles_To(node: HTMLElement, forward: boolean) {
    if (!node) {
      return;
    }
    this.renderer.setStyle(node, 'position', 'absolute');
    this.renderer.setStyle(
      node,
      'transform',
      this._getPreAnimationTransform_To(forward)
    );
    this.renderer.setStyle(node, 'top', '0');
    this.renderer.setStyle(node, 'left', '0');
    this.renderer.setStyle(node, 'opacity', '0');
    this.renderer.setStyle(node, 'width', '100%');
    this.renderer.setStyle(node, 'visibility', 'visible');
  }

  private _postAnimationStyles_To(node: HTMLElement) {
    if (!node) {
      return;
    }
    this.renderer.setStyle(node, 'position', 'relative');
    this.renderer.setStyle(node, 'transform', null);
    this.renderer.setStyle(node, 'opacity', '1');
  }

  private _preAnimationStyles_From(node: HTMLElement, forward: boolean) {
    if (!node) {
      return;
    }
    this.renderer.setStyle(node, 'position', 'absolute');
    this.renderer.setStyle(node, 'transform', 'translateX(0) scale(1)');
    this.renderer.setStyle(node, 'top', '0');
    this.renderer.setStyle(node, 'left', '0');
    this.renderer.setStyle(node, 'width', '100%');
  }

  private _postAnimationStyles_From(node: HTMLElement) {
    if (!node) {
      return;
    }
    this.renderer.setStyle(node, 'display', 'none');
    this.renderer.setStyle(node, 'opacity', '0');
  }

  private _preAnimationStylesSetion_To(node: HTMLElement, forward: boolean) {
    if (!node) {
      return;
    }
    this.renderer.setStyle(node, 'position', 'relative');
    this.renderer.setStyle(
      node,
      'transform',
      this._getPreAnimationTransform_To(forward)
    );
    this.renderer.setStyle(node, 'top', '0');
    this.renderer.setStyle(node, 'left', '0');
    this.renderer.setStyle(node, 'opacity', '0');
  }

  private _getPreAnimationTransform_To(forward) {
    if (forward) {
      return `translateX(32px) scale(.97)`;
    } else {
      return `translateX(-32px) scale(.97)`;
    }
  }

  private _getPostAnimationTransform_From(forward) {
    if (forward) {
      return `translateX(-32px) scale(1)`;
    } else {
      return `translateX(32px) scale(1)`;
    }
  }

  private _setStaticHeight(el: HTMLElement) {
    this.renderer.setStyle(el, 'height', `${el.offsetHeight}px`);
  }

  private _removeStaticHeight(el: HTMLElement) {
    this.renderer.setStyle(el, 'height', null);
  }
}
